import React, { useEffect, useRef, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { dataLimits, S3_URL, USER_TYPES } from 'constants/index';
import { getCreatedDifferenceInBestUnit, unitNumber, withAuth } from 'utils';
import useAuthStore from 'store/auth';
import useCommonStore, { setIsLoading, setModalConfig } from 'store/common';
import {
	logPostLiked,
	useBlockUserMutation,
	useDeleteFeedMutation,
	useGetPostMutation,
	usePostLikeMutation,
	useReportMutation,
} from 'services';
import toast from 'react-hot-toast';
import {
	removeFeedByPostId,
	removeFeedByUserId,
	setPostLikedUsers,
	updateIsLike,
	updateTotalLikes,
} from 'store/feeds';
import { useNavigate } from 'react-router-dom';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';
import SocialPostHeader from './SocialPostHeader';
import SocialPostBody from './SocialPostBody';
import SocialPostActions from './SocialPostActions';

import {
	setFollowingFeeds,
	setFollowingFeedsMeta,
	setFollowingLastcursor,
	setFollowingCurrentCursor,
	removeFollowingFeedByPostId,
} from 'store/feedsFollowing';

import {
	setForYouFeeds,
	setForYouFeedsMeta,
	setForYouLastcursor,
	setForYouCurrentCursor,
	removeForYouFeedByPostId,
	setForYouMyFeeds,
	setMyPostMeta,
	setMyPostCurrentCursor,
	setMyPostLastCursor,
} from 'store/feedsForYou';

const SocialPost: React.FC<any> = ({ post, refetch, type = 'feeds' }) => {
	const navigate = useNavigate();
	const { translate } = useTranslate();
	const { user, token }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const { mutateAsync: postLikeMutation, isPending } = usePostLikeMutation();
	const { mutateAsync: reportFeedsMutation } = useReportMutation();
	const { mutateAsync: blockUserMutation } = useBlockUserMutation();
	const { mutateAsync: deleteFeedMutation } = useDeleteFeedMutation();

	const { mutateAsync: getPostMutation } = useGetPostMutation();

	const {
		postId,
		isLike,
		profilePicture,
		totalComments,
		totalLikes,
		userId,
		userLocation,
		userName,
		userType,
	} = post;

	const [isLiked, setIsLiked] = useState<boolean>(isLike);
	const [likes, setLikes] = useState(Number(totalLikes) || 0);
	const [hasMore, setHasMore] = useState(false);
	const descriptionRef = useRef(null);

	useEffect(() => {
		const element: any = descriptionRef?.current;
		if (element?.scrollHeight > element?.clientHeight) {
			setHasMore(true);
		}
	}, [post?.content]);

	const searchString = `order=DESC&cursor=${0}&limit=${dataLimits?.feedsListing}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected`;
	const searchStringAll = `order=DESC&cursor=${0}&limit=${dataLimits?.feedsListing}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected&allUsers=true`;
	const searchStringPost = `order=DESC&cursor=${0}&limit=${dataLimits?.feedsListing}&socialPostType=my-posts`;

	const handleShow = () => {
		if (totalLikes > 0 || likes > 0) {
			setModalConfig({
				visible: true,
				id: postId,
				type: 'postLikedBy',
				onClose: onCloseLikeModal,
				props: {
					size: 'lg',
					dialogClassName: 'dialog-like-list',
				},
			});
		}
	};

	const onCloseLikeModal = () => {
		setPostLikedUsers([]);
		hideCommonModal();
	};

	const onHandleLike = () => {
		postLikeMutation({ postId: post?.postId })
			.then((res: any) => {
				if (res?.success) {
					if (['hash', 'myfeeds'].includes(type)) {
						if (isLiked) setLikes((prev: any) => prev - 1);
						else setLikes((prev: any) => prev + 1);
						setIsLiked((prev) => !prev);
					} else {
						updateIsLike(post?.postId, isLike ? false : true);
						updateTotalLikes(
							postId,
							isLike ? (Number(totalLikes) > 0 ? totalLikes - 1 : 0) : Number(totalLikes) + 1,
						);
					}
				} else {
					setIsLiked((prev) => !prev);
					toast.error(res?.message);
				}
			})
			.catch((err: any) => {})
			.finally(() => {
				logPostLiked(postId);
			});
	};

	const onCommentClick = () => {
		navigate(`/feed-detail/${postId}#comment-section`);
	};

	const handleModalClick = (payload: any) => {
		reportFeedsMutation(payload)
			.then((res: any) => {
				if (res?.success) {
					removeFeedByPostId(postId);
					removeFollowingFeedByPostId(postId);
					removeForYouFeedByPostId(postId);
				}
			})
			.finally(() => {
				setModalConfig({
					visible: false,
					id: postId,
					onClick: handleModalClick,
					type: 'reportFeeds',
					contentType: 'socialPost',
				});
			});
	};

	const blockUser = () => {
		blockUserMutation(userId)
			.then((res: any) => {
				if (res?.success) {
					toast.success(translate('ToastMessage.userBlockedSuccessfully'));
					removeFeedByUserId(userId);
					hideCommonModal();
				}
			})
			.catch((error) => {
				console.error('Failed to block user:', error);
			});
	};

	const blockUserModal = () => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			onClick: blockUser,
			data: {
				title: translate('Modal.wantToBlockAccount'),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const onHandleDelete = (id: number | null = null) => {
		setModalConfig({
			visible: true,
			id: id,
			type: 'confirmModal',
			onClick: () => onHandleYes(id),
			data: {
				title: translate('Modal.wantToDelete', { title: `${translate('Modal.thisFeed')}` }),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const onHandleYes = (id: number | null) => {
		setIsLoading(true);
		deleteFeedMutation(id)
			.then(async (res: any) => {
				if (res?.success) {
					removeFeedByPostId(Number(id));
					removeFollowingFeedByPostId(Number(id));
					removeForYouFeedByPostId(Number(id));

					const postRes = await getPostMutation(searchString);
					if (postRes?.success) {
						const { docs = {}, meta = {} } = postRes?.data || {};
						const { results = [] } = docs;
						if (results?.length > 0) {
							setFollowingFeeds(results);
							setFollowingFeedsMeta(results.meta);
							setFollowingCurrentCursor(results.meta?.nextCursor || 0);
							setFollowingLastcursor(results.meta?.nextCursor || 0);
						}
					}

					const postResAll = await getPostMutation(searchStringAll);
					if (postResAll?.success) {
						const { docs = {}, meta = {} } = postResAll?.data || {};
						const { results = [] } = docs;
						if (results?.length > 0) {
							setForYouFeeds(results);
							setForYouFeedsMeta(results.meta);
							setForYouCurrentCursor(results.meta?.nextCursor || 0);
							setForYouLastcursor(results.meta?.nextCursor || 0);
						}
					}

					const postResMyPosts = await getPostMutation(searchStringPost);
					if (postResMyPosts?.success) {
						const { docs = {}, meta = {} } = postResMyPosts?.data || {};
						const { results = [] } = docs;
						if (results?.length > 0) {
							setForYouMyFeeds(results);
							setMyPostMeta(results.meta);
							setMyPostCurrentCursor(results.meta?.nextCursor || 0);
							setMyPostLastCursor(results.meta?.nextCursor || 0);
						}
					}
					// refetch();
				} else {
					toast.error(res?.message);
				}
			})
			.finally(() => {
				setIsLoading(false);
				hideCommonModal();
			});
	};

	const redirectToProfile = () => {
		navigate(`/${userType === USER_TYPES?.COMPANY ? 'company' : 'seeker'}/profile/${userId}`);
	};

	return (
		<div className="social-post white-box bg-white">
			<Stack className="social-post-inner">
				<SocialPostHeader
					profilePicture={profilePicture}
					userName={userName}
					userLocation={userLocation}
					created={post?.created ? getCreatedDifferenceInBestUnit(post.created) : ''}
					userType={userType}
					userId={userId}
					token={token}
					withAuth={withAuth}
					redirectToProfile={redirectToProfile}
					postId={postId}
					user={user}
					setModalConfig={setModalConfig}
					handleModalClick={handleModalClick}
					blockUserModal={blockUserModal}
					hideCommonModal={hideCommonModal}
					onHandleDelete={onHandleDelete}
					translate={translate}
				/>
				<SocialPostBody
					content={post?.content}
					taggedUsers={post?.taggedUsers}
					hashTags={post?.hashTags}
					mediaItems={post?.mediaItems}
					hasMore={hasMore}
					descriptionRef={descriptionRef}
					onContentClick={withAuth(token, () => navigate(`/feed-detail/${postId}`))}
				/>
				<SocialPostActions
					isLiked={isLiked}
					isLike={isLike}
					likes={likes}
					totalLikes={totalLikes}
					totalComments={totalComments}
					onLike={onHandleLike}
					onShowLikes={handleShow}
					onComment={onCommentClick}
					onShare={() => {
						setModalConfig({
							visible: true,
							id: Number(postId),
							type: 'postShare',
						});
					}}
					isPending={isPending}
					type={type}
					token={token}
					withAuth={withAuth}
					unitNumber={unitNumber}
					translate={translate}
					postId={postId}
					setModalConfig={setModalConfig}
				/>
			</Stack>
		</div>
	);
};

export default SocialPost;
